import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import { useTranslation } from 'src/arb-wrapper';
import {
  type AssetStatus,
  ASSET_STATUSES,
} from 'src/components/assets/asset-status';
import { StandaloneAssetsContainer } from 'src/components/assets/StandaloneAssetsContainer';
import { Breadcrumbs } from 'src/components/common/Breadcrumbs';

const VALID_TABS: ReadonlySet<string> = new Set<AssetStatus>([
  ASSET_STATUSES.PENDING,
  ASSET_STATUSES.APPROVED,
  ASSET_STATUSES.REJECTED,
]);

/** Maps an AssetStatus to its i18n translation key for breadcrumb display. */
const TAB_LABEL_KEYS: Record<AssetStatus, string> = {
  PENDING: 'asset_status_pending',
  APPROVED: 'asset_status_approved',
  REJECTED: 'asset_status_rejected',
};

function parseTabParam (value: string | null): AssetStatus {
  if (value && VALID_TABS.has(value)) {
    return value as AssetStatus;
  }
  return ASSET_STATUSES.PENDING;
}

export const StandaloneAssets = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const approvalStatus = parseTabParam(searchParams.get('tab'));

  const handleTabChange = useCallback((tab: AssetStatus) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === ASSET_STATUSES.PENDING) {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <Column alignmentHorizontal="stretch" heights="fill">
      <Breadcrumbs
        items={[
          { label: t('assets'), path: '/assets' },
          { label: t(TAB_LABEL_KEYS[approvalStatus]) },
        ]}
      />
      <Heading level={1} type="h500">
        {t('assets')}
      </Heading>
      <StandaloneAssetsContainer
        approvalStatus={approvalStatus}
        onTabChange={handleTabChange}
      />
    </Column>
  );
};
